<template>
    <div id="visitorList">
        <supsis-table
            :columns="cols()"
            :rows="rows"
            :bottom-pagination="true"
            :pagination-cursor="paginationCursor"
            @cursor-changed="(cursor) => (paginationCursor = cursor)"
            :options="tableOptions"
        />
    </div>
</template>

<script>
import * as _ from "lodash";
import { log2 } from "../utils/log2";
import Pagination from "./Pagination";
import { sendSocketMessage, socketMessageStream } from "../utils/socketStream";
import { filter, map } from "rxjs/operators";
import { mapActions, mapGetters, mapState } from "vuex";
import SYSTEM_PUBSUB from "../utils/systemPubSub";
import WatchIcon from "./svg/WatchIcon";
import SupsisTable from "./SupsisTable.vue";
import ChatWatcher from "../ChatWatcher";
import { isMaskContactInfo, maskContactInfo } from "../utils/utils";

const log = log2("VisitorList");
export default {
    name: "VisitorList",
    components: {
        SupsisTable,
        Pagination,
        WatchIcon,
    },
    props: {},
    data() {
        return {
            rows: [],
            currentSort: {
                columnKey: "connectionTime",
                order: "ascend",
            },
            cols: () => [
                {
                    title: this.$t("message.fullname"),
                    field: "fullname",
                    key: "fullname",
                    width: 200,
                    fixed: "left",
                },
                {
                    title: this.$t("message.email"),
                    field: "email",
                    key: "email",
                    width: 200,
                    fixed: "left",
                },
                {
                    title: this.$t("message.country"),
                    field: "country",
                    key: "country",
                    width: 200,
                },
                {
                    title: this.$t("message.city"),
                    field: "city",
                    key: "city",
                    width: 200,
                },
                {
                    title: this.$t("message.browser"),
                    field: "browser",
                    key: "browser",
                    width: 200,
                },
                {
                    title: this.$t("message.os"),
                    field: "os",
                    key: "os",
                    width: 200,
                },
                {
                    title: this.$t("message.connectionDate"),
                    field: "connectionTime",
                    key: "connectionTime",
                    width: 200,
                    renderBodyCell: ({ row }) => {
                        return this.$options.filters.date(row.connectionTime);
                    },
                },
                {
                    title: this.$t("message.visitingPage"),
                    field: "visitingPage",
                    key: "visitingPage",
                    width: 400,
                    renderBodyCell: ({ row }) => {
                        return (
                            <div
                                v-tippy={{
                                    followCursor: true,
                                    placement: "bottom-end",
                                    theme: this.isDarkMode ? "light-border" : "google",
                                    zIndex: 99999999999999,
                                }}
                                content={row.visitingPage}
                            >
                                {row.visitingPage ? (
                                    <a class="webAdressMask" target="_blank" href={row.visitingPage}>
                                        {row.visitingPage}
                                    </a>
                                ) : (
                                    <a class="webAdressMask">-</a>
                                )}
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("message.transactions"),
                    field: "actions",
                    key: "actions",
                    width: 140,
                    align: "right",
                    fixed: "right",
                    renderBodyCell: ({ row }) => {
                        const activeConversation = !!row.lastConversationId;
                        return (
                            !this.isAdmin &&
                            (activeConversation ? (
                                <div class="d-flex justify-content-end" style="margin-right: -10px !important">
                                    <md-button
                                        id="joinChat"
                                        v-tippy={{
                                            followCursor: true,
                                            placement: "bottom-end",
                                            theme: this.isDarkMode ? "light-border" : "google",
                                        }}
                                        content={this.$t("message.watchChat")}
                                        className="custom-button"
                                        data-v-step="16"
                                        on-click={() => this.observeContact(row._id)}
                                    >
                                        <a-icon style="font-size: 14px; color: #49c550" theme="filled" type="eye" />
                                    </md-button>
                                </div>
                            ) : (
                                <div class="d-flex justify-content-end" style="margin-right: -10px !important">
                                    <md-button
                                        id="joinChat"
                                        v-tippy={{
                                            followCursor: true,
                                            placement: "bottom-end",
                                            theme: this.isDarkMode ? "light-border" : "google",
                                        }}
                                        content={this.$t("message.startChatDesc")}
                                        class="custom-button"
                                        data-v-step="16"
                                        on-click={() => this.startChat(row._id)}
                                    >
                                        <a-icon style="font-size: 14px; color: #49c550" theme="filled" type="message" />
                                    </md-button>
                                </div>
                            ))
                        );
                    },
                },
            ],
            tableOptions: {
                rowStyleOption: {
                    stripe: true,
                },
                rowKeyFieldName: "_id",
                class: "visitor-list-table",
                borderX: true,
                borderY: true,
                scrollWidth: 1130,
                virtualScrollOptions: { itemSize: 50, height: 500 },
            },
            loading: false,
            dataFetcherSubscriber: null,
            anotherMyActiveConversations: [],
            paginationCursor: {
                total: 0,
                page: 1,
                perPage: 10,
            },
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
            liveVisitorCount: (state) => state.visitor.liveVisitorCount,
            conversations: (state) => state.chat.latestRows,
            isAdmin: (state) => state.auth.adminUser != null,
            observing: (state) => state.chat.observing,
            allWebHooks: (state) => state.customerWebhook.allWebHooks,
            maskSensitiveInfo: (state) => state.setting.settings.maskSensitiveInfo,
            isRoot: (state) => _.get(state, "auth.user.role.isRoot", false),
        }),
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
            myActiveConversations: "chat/getMyActiveConversations",
        }),
    },
    mounted() {
        log("mounted");
        this.anotherMyActiveConversations = this.myActiveConversations;
        this.dataFetcherSubscriber = socketMessageStream
            .pipe(
                filter((x) => x.cmd === "live_visitors_response"),
                map((x) => x.data)
            )
            .subscribe((data) => {
                const { visitors, total } = data;
                this.rows = visitors.map((visitor) => {
                    visitor.fullname = this.maskContactInfo(visitor, "fullname", 5);
                    visitor.email = this.maskContactInfo(visitor, "email", 5);
                    visitor.phone = this.maskContactInfo(visitor, "phone", 5);
                    visitor.whatsappNumber = this.maskContactInfo(visitor, "whatsappNumber", 5);
                    return visitor;
                });
                this.paginationCursor.total = total;
                this.loading = false;
            });
        this.timer = setInterval(() => this.sendRequest(), 5000);
        this.fetchPagedData();
    },
    beforeDestroy() {
        if (this.dataFetcherSubscriber) this.dataFetcherSubscriber.unsubscribe();
        if (this.timer) clearInterval(this.timer);
    },
    methods: {
        ...mapActions({
            joinConversation: "chat/joinConversation",
            fetchUserWebHooks: "customerWebhook/fetchUserWebHooks",
        }),
        isMaskContactInfo,
        isShouldMask(visitor, val) {
            return (
                !this.isRoot &&
                this.maskSensitiveInfo &&
                (val === visitor.email || val === visitor.whatsappNumber || val === visitor.phone)
            );
        },
        maskContactInfo(visitor, key, length = 4) {
            return this.isMaskContactInfo(key)
                ? maskContactInfo(visitor[key], length)
                : this.isShouldMask(visitor, visitor[key])
                ? maskContactInfo(visitor[key], length)
                : visitor[key];
        },
        observeContact(contactId) {
            ChatWatcher.observeContact(contactId);
        },
        startChat(visitorId) {
            ChatWatcher.startChatImmediately({
                visitorId,
            });
        },
        sendRequest() {
            sendSocketMessage("user get-live-visitors", {
                page: this.paginationCursor.page - 1,
                perPage: this.paginationCursor.perPage,
                sortCol: this.currentSort.columnKey,
                sortDirection: this.currentSort.order === "ascend" ? "ASC" : "DESC",
                userId: this.isAdmin ? undefined : this.user._id,
                siteId: this.site._id,
            });
        },
        fetchPagedData() {
            this.loading = true;
            this.sendRequest();
        },
    },
    watch: {
        myActiveConversations: {
            handler(val) {
                this.anotherMyActiveConversations = this.myActiveConversations;
            },
            deep: true,
        },
        paginationCursor: {
            deep: true,
            handler(newVal, oldVal) {
                newVal = { page: newVal.page, perPage: newVal.perPage };
                oldVal = { page: oldVal.page, perPage: oldVal.perPage };
                if (!_.isEqual(newVal, oldVal)) {
                    this.fetchPagedData();
                }
            },
        },
        async liveVisitorCount(newValue) {
            if (newValue) {
                const total = newValue;
                const maxPage = Math.ceil(total / this.paginationCursor.perPage);
                if (this.paginationCursor.page > maxPage) {
                    this.paginationCursor.page = 0;
                } else {
                    if (this.rows.length < this.paginationCursor.perPage) {
                        this.fetchPagedData();
                    }
                }
                if (total < this.paginationCursor.perPage) {
                    this.fetchPagedData();
                }
            }
        },
    },
};
</script>

<style lang="scss">
#visitorList {
    padding: 10px;

    .supsis-table {
        tr {
            height: max-content !important;
        }

        td {
            padding: 1px 5px !important;
        }

        .custom-button {
            max-width: 18px;
            min-width: 18px;
            max-height: 18px;
            border-radius: 50%;
            overflow: hidden;
            display: flex;
            justify-content: center;
            align-items: center;

            svg {
                width: 14px;
                height: 14px;
            }
        }
    }

    .webAdressMask {
        overflow: hidden;
        text-overflow: ellipsis;
        -webkit-box-orient: vertical;
        white-space: nowrap;
        -webkit-line-clamp: 1;
        max-width: 300px;
        word-break: break-all;
    }
}
</style>
